/**
 * Serviço centralizado de tratamento de erros
 * Converte erros técnicos em mensagens amigáveis para o usuário
 */

/**
 * Tipos de erro conhecidos
 */
export type ErrorType = 
  | 'network'           // Erro de rede/conexão
  | 'server'            // Erro do servidor (5xx)
  | 'auth'              // Erro de autenticação
  | 'not_found'         // Recurso não encontrado
  | 'validation'        // Erro de validação
  | 'timeout'           // Timeout de requisição
  | 'config'            // Erro de configuração
  | 'unknown'           // Erro desconhecido

/**
 * Estrutura do erro tratado
 */
export interface FriendlyError {
  type: ErrorType
  title: string
  message: string
  suggestion?: string
  canRetry: boolean
  originalError?: Error
}

/**
 * Mensagens amigáveis por tipo de erro
 */
const ERROR_MESSAGES: Record<ErrorType, { title: string; message: string; suggestion: string }> = {
  network: {
    title: 'Falha na conexão',
    message: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
    suggestion: 'Tente novamente em alguns instantes ou verifique se você está conectado à internet.'
  },
  server: {
    title: 'Erro no servidor',
    message: 'O servidor encontrou um problema ao processar sua solicitação.',
    suggestion: 'Por favor, aguarde alguns minutos e tente novamente. Se o problema persistir, entre em contato com o suporte.'
  },
  auth: {
    title: 'Erro de autenticação',
    message: 'Sua sessão expirou ou você não tem permissão para acessar este recurso.',
    suggestion: 'Tente fazer login novamente ou verifique suas credenciais.'
  },
  not_found: {
    title: 'Recurso não encontrado',
    message: 'O arquivo ou recurso solicitado não foi encontrado.',
    suggestion: 'Verifique se o arquivo ainda existe ou se o link está correto.'
  },
  validation: {
    title: 'Dados inválidos',
    message: 'Os dados fornecidos não são válidos.',
    suggestion: 'Verifique os dados informados e tente novamente.'
  },
  timeout: {
    title: 'Tempo esgotado',
    message: 'A operação demorou muito para ser concluída.',
    suggestion: 'O servidor pode estar sobrecarregado. Tente novamente em alguns minutos.'
  },
  config: {
    title: 'Erro de configuração',
    message: 'A aplicação não está configurada corretamente.',
    suggestion: 'Entre em contato com o administrador do sistema.'
  },
  unknown: {
    title: 'Erro inesperado',
    message: 'Ocorreu um erro inesperado.',
    suggestion: 'Tente novamente. Se o problema persistir, entre em contato com o suporte.'
  }
}

/**
 * Detecta o tipo de erro baseado na mensagem ou objeto de erro
 */
function detectErrorType(error: any): ErrorType {
  const message = (error?.message || error?.toString() || '').toLowerCase()
  
  // Erro de rede/fetch
  if (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('net::') ||
    message.includes('networkerror') ||
    message.includes('cors') ||
    message.includes('connection refused') ||
    message.includes('econnrefused') ||
    message.includes('offline') ||
    message.includes('fetch failed') ||
    error?.name === 'TypeError' && message.includes('fetch')
  ) {
    return 'network'
  }
  
  // Timeout
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('abort') ||
    error?.name === 'AbortError'
  ) {
    return 'timeout'
  }
  
  // Autenticação
  if (
    message.includes('unauthorized') ||
    message.includes('401') ||
    message.includes('forbidden') ||
    message.includes('403') ||
    message.includes('token') ||
    message.includes('authentication') ||
    message.includes('sessão') ||
    message.includes('session')
  ) {
    return 'auth'
  }
  
  // Não encontrado
  if (
    message.includes('not found') ||
    message.includes('404') ||
    message.includes('não encontrado') ||
    message.includes('does not exist')
  ) {
    return 'not_found'
  }
  
  // Erro de servidor
  if (
    message.includes('500') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('504') ||
    message.includes('internal server') ||
    message.includes('bad gateway') ||
    message.includes('service unavailable')
  ) {
    return 'server'
  }
  
  // Erro de validação
  if (
    message.includes('validation') ||
    message.includes('invalid') ||
    message.includes('required') ||
    message.includes('inválido')
  ) {
    return 'validation'
  }
  
  // Erro de configuração
  if (
    message.includes('configuração') ||
    message.includes('configuration') ||
    message.includes('config') ||
    message.includes('ausente') ||
    message.includes('missing')
  ) {
    return 'config'
  }
  
  return 'unknown'
}

/**
 * Converte um erro em uma mensagem amigável para o usuário
 */
export function handleError(error: any): FriendlyError {
  const type = detectErrorType(error)
  const config = ERROR_MESSAGES[type]
  
  // Se o backend retornou uma mensagem específica e não é técnica, usar ela
  const backendMessage = error?.message || ''
  const shouldUseBackendMessage = 
    backendMessage && 
    !backendMessage.toLowerCase().includes('failed to fetch') &&
    !backendMessage.toLowerCase().includes('network') &&
    !backendMessage.toLowerCase().startsWith('http') &&
    backendMessage.length < 200

  return {
    type,
    title: config.title,
    message: shouldUseBackendMessage && type !== 'network' && type !== 'timeout' 
      ? backendMessage 
      : config.message,
    suggestion: config.suggestion,
    canRetry: ['network', 'server', 'timeout'].includes(type),
    originalError: error instanceof Error ? error : new Error(String(error))
  }
}

/**
 * Formata erro para exibição simples (apenas mensagem)
 */
export function getErrorMessage(error: any): string {
  const friendly = handleError(error)
  return friendly.message
}

/**
 * Formata erro para exibição com título
 */
export function getErrorWithTitle(error: any): { title: string; message: string } {
  const friendly = handleError(error)
  return {
    title: friendly.title,
    message: friendly.message
  }
}

/**
 * Verifica se o erro permite retry
 */
export function canRetry(error: any): boolean {
  return handleError(error).canRetry
}

/**
 * Erros específicos por contexto para mensagens mais precisas
 */
export const contextualErrors = {
  listFiles: (error: any) => {
    const base = handleError(error)
    if (base.type === 'network') {
      return {
        ...base,
        message: 'Não foi possível carregar a lista de arquivos. O servidor pode estar temporariamente indisponível.',
        suggestion: 'Verifique sua conexão e clique em "Atualizar" para tentar novamente.'
      }
    }
    return base
  },
  
  upload: (error: any) => {
    const base = handleError(error)
    if (base.type === 'network') {
      return {
        ...base,
        message: 'Falha ao enviar o arquivo. A conexão foi interrompida durante o upload.',
        suggestion: 'Verifique sua conexão e tente enviar o arquivo novamente.'
      }
    }
    if (base.type === 'validation') {
      return {
        ...base,
        message: 'O arquivo enviado não é válido ou não é suportado.',
        suggestion: 'Verifique se o arquivo é um áudio ou vídeo nos formatos: MP3, WAV, M4A, OGG ou MP4.'
      }
    }
    return base
  },
  
  download: (error: any) => {
    const base = handleError(error)
    if (base.type === 'not_found') {
      return {
        ...base,
        message: 'O arquivo solicitado não foi encontrado ou já foi removido.',
        suggestion: 'Atualize a lista de arquivos para verificar o status.'
      }
    }
    return base
  },
  
  delete: (error: any) => {
    const base = handleError(error)
    if (base.type === 'not_found') {
      return {
        ...base,
        message: 'O arquivo já foi removido.',
        suggestion: 'Atualize a lista de arquivos.'
      }
    }
    return base
  },
  
  analysis: (error: any) => {
    const base = handleError(error)
    if (base.type === 'network') {
      return {
        ...base,
        message: 'Não foi possível carregar a análise. O servidor de IA pode estar temporariamente indisponível.',
        suggestion: 'Aguarde alguns instantes e tente novamente.'
      }
    }
    if (base.type === 'not_found') {
      return {
        ...base,
        message: 'A transcrição deste arquivo não está disponível.',
        suggestion: 'Verifique se o arquivo foi processado corretamente.'
      }
    }
    return base
  },
  
  transcription: (error: any) => {
    const base = handleError(error)
    if (base.type === 'network') {
      return {
        ...base,
        message: 'Não foi possível carregar a transcrição. Verifique sua conexão com a internet.',
        suggestion: 'Clique em "Voltar" e tente abrir o arquivo novamente.'
      }
    }
    return base
  },
  
  comparison: (error: any) => {
    const base = handleError(error)
    if (base.type === 'network') {
      return {
        ...base,
        message: 'Não foi possível realizar a comparação. Verifique sua conexão.',
        suggestion: 'Tente novamente em alguns instantes.'
      }
    }
    return base
  }
}

export default {
  handleError,
  getErrorMessage,
  getErrorWithTitle,
  canRetry,
  contextualErrors
}
